import { RefObject, useLayoutEffect } from "react";
import RunwayOverlayWhite from "../assets/unionPointRunwayMap.svg";
import { VehicleRouteInfo } from "../../Home/assets/ClosedLoopRoutes";

const convertToWaypoint = (latLng: [number, number]) => (
    { location: new google.maps.LatLng(...latLng) }
);

const setUnionPointOverlay = () => {
    const NE_BOUND = new google.maps.LatLng(42.15378672405888, -70.9372013847323);
    const SW_BOUND = new google.maps.LatLng(42.15086193718102, -70.94154422157715);

    return new google.maps.GroundOverlay(
        RunwayOverlayWhite,
        new google.maps.LatLngBounds(SW_BOUND, NE_BOUND),
    );
};

// We send the directional service a set of way points to get the route information
// which the directional renderer draws on the map
const drawRoutes = async (
    routeDetails: VehicleRouteInfo[],
    googleMaps: google.maps.Map,
) => {
    const { OK } = google.maps.DirectionsStatus;

    for (let i = 0; i < routeDetails.length; i += 1) {
        const {
            start, end, wayPoints, type,
        } = routeDetails[i];
        const dirRenderer = new google.maps.DirectionsRenderer({
            suppressMarkers: true,
            preserveViewport: true,
        });
        const dirService = new google.maps.DirectionsService();

        dirRenderer.setMap(googleMaps);

        // Due to the fact when you draw routes google likes to zoom in on that part of the map
        // The directions need to be synchronous to handle the BNY loop as there are two parts to it
        // Otherwise the map may center on the docks as that may resolve last.

        // eslint-disable-next-line no-await-in-loop
        await dirService.route({
            origin: new google.maps.LatLng(...start),
            destination: new google.maps.LatLng(...end),
            travelMode: type || google.maps.TravelMode.DRIVING,
            waypoints: wayPoints.map(convertToWaypoint),
        }, (result, status) => {
            if (status === OK) {
                // Draws the route onto the map
                dirRenderer.setDirections(result);
            }
        });
    }
};

export const useGoogleMaps = (
    mapRef: RefObject<HTMLDivElement>,
    googleMapRef: RefObject<google.maps.Map>,
    cleanupRefs: () => void,
    options?: google.maps.MapOptions,
    staticRouteInfo?: VehicleRouteInfo[],
    center?: google.maps.LatLngLiteral,
) => {
    useLayoutEffect(() => () => cleanupRefs(), []);

    // Everytime the options are updated, we see if the googleMapRef is defined
    // If so we just update the options
    // If not we declare a new google map object and assign it to the ref
    useLayoutEffect(() => {
        if (mapRef.current && options) {
            cleanupRefs();
            if (googleMapRef.current) {
                googleMapRef.current.setOptions(options);
            } else {
                (googleMapRef.current as unknown) = new google.maps.Map(
                    mapRef.current,
                    options,
                );
            }

            setUnionPointOverlay().setMap(googleMapRef.current);
        }
    }, [options]);

    useLayoutEffect(() => {
        if (
            googleMapRef.current
            && staticRouteInfo
            && staticRouteInfo.length > 0
        ) {
            drawRoutes(staticRouteInfo, googleMapRef.current as google.maps.Map);
        }
    }, [staticRouteInfo]);

    // To achieve the pan affect we check if the center passed in is updated and pan to it
    useLayoutEffect(() => {
        if (googleMapRef.current && center) {
            googleMapRef.current.panTo(center);
        }
    }, [center]);
};
